import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, InfoNote, SyntheticChip } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { FlaskConical, CheckCircle2, XCircle, Play } from "lucide-react";

const STATUS_CLS = {
  SATISFIED: "bg-emerald-50 text-emerald-700 border-emerald-300",
  MISSING: "bg-amber-50 text-amber-800 border-amber-300",
  REVIEW_REQUIRED: "bg-slate-100 text-slate-600 border-slate-300",
};
function StatusTag({ s }) {
  return <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${STATUS_CLS[s] || "bg-slate-100 text-slate-600 border-slate-300"}`}>{(s || "").replace(/_/g, " ")}</span>;
}

export default function RulesHarness() {
  const [sample, setSample] = useState(null);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.harnessSample().then(setSample).catch(() => {}); }, []);

  const runHarness = async () => {
    setBusy(true);
    try { const r = await api.runHarness({}); setRun(r); }
    finally { setBusy(false); }
  };

  const rule = sample?.rule;
  const cases = sample?.cases || [];
  const rows = run?.results || cases;
  const pct = run ? Math.round((run.passed / run.total) * 100) : 0;

  return (
    <div>
      <PageHeader testid="rules-harness-header" title="Rule Validation Harness"
        subtitle="Translate domain knowledge into testable requirements. Every rule is checked against synthetic scenarios with known expected outcomes before it can be considered validated."
        actions={<Button size="sm" className="bg-teal-600 hover:bg-teal-700" data-testid="harness-run" disabled={busy || !sample} onClick={runHarness}><Play className="mr-1.5 h-4 w-4" /> Run Harness</Button>}
      />
      <InfoNote className="mb-4">Synthetic harness only. Passing the harness is a prerequisite for domain validation — it does not, by itself, approve a rule for live use.</InfoNote>

      {rule && (
        <Card className="mb-4 p-4" data-testid="harness-rule">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800"><FlaskConical className="h-4 w-4 text-slate-400" /> Rule under test <SyntheticChip /></div>
          <dl className="grid gap-x-8 gap-y-1 text-xs text-slate-600 sm:grid-cols-2">
            <Row k="Criterion" v={rule.criterion_code} /><Row k="Type" v={rule.rule_type} />
            <Row k="Description" v={rule.description} /><Row k="Required fact" v={rule.required_fact_type} />
            <Row k="Severity" v={rule.severity} /><Row k="Confidence threshold" v={rule.threshold} />
          </dl>
        </Card>
      )}

      <Card className="p-4" data-testid="harness-cases">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-semibold text-slate-800">Synthetic Test Scenarios</div>
          {run && (
            <div className="flex items-center gap-3">
              <div className="w-40"><Progress value={pct} className="h-2" /></div>
              <span className={`text-sm font-bold ${run.passed === run.total ? "text-emerald-700" : "text-amber-700"}`} data-testid="harness-score">{run.passed}/{run.total} passing</span>
            </div>
          )}
        </div>
        <div className="space-y-2">
          {rows.map((c, i) => {
            const res = run?.results ? c : null;
            return (
              <div key={i} className="rounded-md border border-slate-200 p-3" data-testid={`harness-case-${i}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm text-slate-800">{c.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-400">expected</span> <StatusTag s={c.expected} />
                    {res && (
                      <>
                        <span className="text-[10px] text-slate-400">actual</span> <StatusTag s={res.actual} />
                        {res.pass ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}
                      </>
                    )}
                  </div>
                </div>
                {res?.reason && <div className="mt-1 text-xs text-slate-500">{res.reason}</div>}
                {c.facts?.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {c.facts.map((f, j) => <span key={j} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">“{f.source_quote}” · conf {Math.round(f.confidence * 100)}%</span>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {run && <div className="mt-3 rounded bg-slate-50 p-2 text-[11px] text-slate-500">{run.note}</div>}
      </Card>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4"><dt className="text-slate-400">{k}</dt><dd className="font-medium text-slate-700">{String(v ?? "—")}</dd></div>
);
